import type {
  MarketplaceAddressFormValues,
  MarketplaceAddressOption,
  MarketplaceCheckoutAddresses,
  WooAddress,
  WooCustomer,
  WooOrder,
  WooOrderMetaEntry,
} from "../types";

const STRICT_ADDRESS_FIELDS: (keyof WooAddress)[] = [
  "first_name",
  "last_name",
  "address_1",
  "city",
  "state",
  "postcode",
  "country",
  "phone",
  "email",
];

const REGISTERED_ADDRESS_FIELDS: (keyof WooAddress)[] = [
  "address_1",
  "city",
  "state",
  "postcode",
];

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizePostcode(value?: string): string {
  return (value ?? "").replace(/\D/g, "");
}

export function isAddressComplete(address?: Partial<WooAddress>): boolean {
  if (!address) return false;
  return STRICT_ADDRESS_FIELDS.every((field) => hasText(address[field]));
}

/** Endereço Woo utilizável no checkout — critério relaxado (sem phone/email). */
export function isRegisteredWooAddressComplete(
  address?: Partial<WooAddress>,
): boolean {
  if (!address) return false;

  const hasCoreFields = REGISTERED_ADDRESS_FIELDS.every((field) =>
    hasText(address[field]),
  );
  if (!hasCoreFields) return false;

  const postcode = normalizePostcode(address.postcode);
  if (postcode.length !== 8) return false;

  const country = address.country?.trim() || "BR";
  if (!country) return false;

  return hasText(address.first_name) || hasText(address.last_name);
}

export function customerNeedsAddress(customer: WooCustomer): boolean {
  return !customerHasRegisteredAddress(customer);
}

/** Preferir billing; fallback shipping. */
export function getCustomerRegisteredAddress(
  customer: WooCustomer,
): WooAddress | null {
  if (isRegisteredWooAddressComplete(customer.billing)) {
    return customer.billing;
  }
  if (isRegisteredWooAddressComplete(customer.shipping)) {
    return customer.shipping;
  }
  return null;
}

export function customerHasRegisteredAddress(customer: WooCustomer): boolean {
  return getCustomerRegisteredAddress(customer) !== null;
}

export function splitFullName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

export function parseAddressLine(address1: string): {
  street: string;
  number: string;
} {
  const match = address1.match(/^(.+?),\s*(\S+)$/);
  if (match) {
    return { street: match[1], number: match[2] };
  }
  return { street: address1, number: "" };
}

export function buildAddressLine(street: string, number: string): string {
  const s = street.trim();
  const n = number.trim();
  if (!s) return "";
  if (!n) return s;
  return `${s}, ${n}`;
}

/** Máximo de endereços salvos exibidos no checkout (mais recentes, sem repetir). */
export const MAX_CHECKOUT_SAVED_ADDRESSES = 3;

/** Valor enviado ao Woo/Pagarme quando o imóvel não tem numeração. */
export const BRAZIL_NO_STREET_NUMBER = "S/N";

const NO_STREET_NUMBER_PATTERN = /^(s\/n|sn|sem\s*n[uú]mero?)$/i;

/** Normaliza número para meta Woo/Pagarme (aceita s/n, sn, sem número). */
export function normalizeStreetNumber(
  raw: string,
  options?: { noStreetNumber?: boolean },
): string {
  if (options?.noStreetNumber) return BRAZIL_NO_STREET_NUMBER;

  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (NO_STREET_NUMBER_PATTERN.test(trimmed)) return BRAZIL_NO_STREET_NUMBER;
  return trimmed;
}

export function isStreetNumberValidForCheckout(
  values: Pick<MarketplaceAddressFormValues, "number" | "noStreetNumber">,
): boolean {
  if (values.noStreetNumber) return true;
  return Boolean(values.number.trim());
}

function mapWooAddressToFormValues(
  address: WooAddress,
  customer: WooCustomer,
): MarketplaceAddressFormValues {
  const { street: parsedStreet, number: parsedNumber } = parseAddressLine(
    address.address_1 ?? "",
  );
  const street = parsedStreet.trim() || address.address_1?.trim() || "";
  const rawNumber = address.number?.trim() || parsedNumber;
  const number = normalizeStreetNumber(rawNumber);
  const noStreetNumber = number === BRAZIL_NO_STREET_NUMBER;

  return {
    firstName: address.first_name || customer.first_name || "",
    lastName: address.last_name || customer.last_name || "",
    cpf: address.cpf ?? "",
    phone: address.phone || address.cellphone || "",
    cep: address.postcode ?? "",
    street,
    number,
    noStreetNumber,
    complement: address.address_2 ?? "",
    neighborhood: address.neighborhood?.trim() ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
  };
}

export function customerToFormValues(
  customer: WooCustomer,
  _userEmail: string,
): MarketplaceAddressFormValues {
  const registered = getCustomerRegisteredAddress(customer);
  if (registered) {
    return mapWooAddressToFormValues(registered, customer);
  }

  return {
    ...emptyAddressFormValues(),
    firstName: customer.first_name || customer.billing?.first_name || "",
    lastName: customer.last_name || customer.billing?.last_name || "",
    phone: customer.billing?.phone || customer.billing?.cellphone || "",
    cpf: customer.billing?.cpf ?? "",
  };
}

export function formValuesToWooAddress(
  values: MarketplaceAddressFormValues,
  email: string,
): WooAddress {
  const street = values.street.trim();
  const number = normalizeStreetNumber(values.number, {
    noStreetNumber: values.noStreetNumber,
  });
  const neighborhood = values.neighborhood.trim();

  const cpfDigits = values.cpf.replace(/\D/g, "");

  return {
    first_name: values.firstName.trim(),
    last_name: values.lastName.trim(),
    address_1: buildAddressLine(street, number) || street,
    address_2: values.complement.trim() || undefined,
    city: values.city.trim(),
    state: values.state.trim(),
    postcode: values.cep.replace(/\D/g, ""),
    country: "BR",
    email: email.trim(),
    phone: values.phone.replace(/\D/g, ""),
    cpf: cpfDigits || undefined,
    persontype: cpfDigits ? "F" : undefined,
    number: number || undefined,
    neighborhood: neighborhood || undefined,
  };
}

/**
 * Metadados exigidos pelo plugin WooCommerce Extra Checkout Fields for Brazil.
 * billing.cpf no POST é ignorado; o plugin lê _billing_cpf em meta_data.
 */
export function buildBrazilOrderMetaData(
  billing: WooAddress,
  shipping?: WooAddress,
): WooOrderMetaEntry[] {
  const meta: WooOrderMetaEntry[] = [];
  const cpfDigits = (billing.cpf ?? "").replace(/\D/g, "");
  const phoneDigits = (billing.cellphone ?? billing.phone ?? "").replace(/\D/g, "");
  const billingNumber = normalizeStreetNumber(billing.number ?? "");
  const billingNeighborhood = billing.neighborhood?.trim() ?? "";
  const shippingNumber = normalizeStreetNumber(shipping?.number ?? "");
  const shippingNeighborhood = shipping?.neighborhood?.trim() ?? "";

  if (cpfDigits) {
    meta.push({ key: "_billing_cpf", value: cpfDigits });
    meta.push({ key: "_billing_persontype", value: "1" });
  }

  if (billingNumber) {
    meta.push({ key: "_billing_number", value: billingNumber });
  }
  if (billingNeighborhood) {
    meta.push({ key: "_billing_neighborhood", value: billingNeighborhood });
  }
  if (phoneDigits) {
    meta.push({ key: "_billing_cellphone", value: phoneDigits });
  }

  if (shippingNumber) {
    meta.push({ key: "_shipping_number", value: shippingNumber });
  }
  if (shippingNeighborhood) {
    meta.push({ key: "_shipping_neighborhood", value: shippingNeighborhood });
  }

  return meta;
}

const DEFAULT_PRICE_CURRENCY_CODE = "BRL";

/** Preço de produto Woo com a moeda da loja (`code` do adapter). Código inválido cai em BRL. */
export function formatWooPrice(
  value: string | number,
  currencyCode: string,
): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "—";

  const code = currencyCode.trim().toUpperCase() || DEFAULT_PRICE_CURRENCY_CODE;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: code,
    }).format(num);
  } catch {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: DEFAULT_PRICE_CURRENCY_CODE,
    }).format(num);
  }
}

export function formatPriceBrl(value: string | number): string {
  return formatWooPrice(value, DEFAULT_PRICE_CURRENCY_CODE);
}

type PatientProfileShape = {
  name?: {
    given?: string[];
    family?: string;
    text?: string;
    use?: string;
  }[];
  identifier?: {
    system?: string;
    value?: string;
    type?: { coding?: { code?: string }[] };
  }[];
  telecom?: { system?: string; value?: string; use?: string }[];
  address?: {
    postalCode?: string;
    city?: string;
    state?: string;
    district?: string;
    line?: string[];
    use?: string;
  }[];
};

const CPF_IDENTIFIER_SYSTEMS = [
  "2.16.840.1.113883.13.237",
  "urn:oid:2.16.840.1.113883.13.237",
];

function extractPatientCpf(
  patient: PatientProfileShape,
  fallbackCpf?: string,
): string {
  const fromIdentifier = patient.identifier?.find(
    (id) =>
      CPF_IDENTIFIER_SYSTEMS.some((sys) => id.system?.includes(sys)) ||
      id.type?.coding?.some((c) => c.code === "TAX"),
  )?.value;

  const raw = (fromIdentifier ?? fallbackCpf ?? "").replace(/\D/g, "");
  return raw;
}

function extractPatientName(
  patient: PatientProfileShape,
  userName?: string,
): { firstName: string; lastName: string } {
  if (userName?.trim()) return splitFullName(userName);

  const official =
    patient.name?.find((n) => n.use === "official") ?? patient.name?.[0];

  if (official?.given?.length || official?.family) {
    return {
      firstName: official.given?.join(" ") ?? "",
      lastName: official.family ?? "",
    };
  }

  if (official?.text?.trim()) return splitFullName(official.text);

  return { firstName: "", lastName: "" };
}

function extractPatientPhone(patient: PatientProfileShape): string {
  const phone =
    patient.telecom?.find((t) => t.system === "phone")?.value ??
    patient.telecom?.find((t) => t.system === "sms")?.value ??
    patient.telecom?.find((t) => t.use === "mobile")?.value ??
    patient.telecom?.[0]?.value ??
    "";

  return phone;
}

function extractPatientAddressFields(
  patient: PatientProfileShape,
): Partial<MarketplaceAddressFormValues> {
  const address =
    patient.address?.find((a) => a.use === "home") ?? patient.address?.[0];
  if (!address) return {};

  const line = address.line ?? [];

  return {
    cep: address.postalCode ?? "",
    street: line[0] ?? "",
    number: line[1] ?? "",
    complement: line[2] ?? "",
    neighborhood: address.district ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
  };
}

/**
 * Extrai do perfil FHIR Patient tudo que puder (nome, CPF, telefone, endereço parcial).
 * Não exige endereço completo — útil para pré-preencher "novo endereço".
 */
export function patientProfilePrefillFormValues(
  userPatient: unknown,
  options?: { userName?: string; patientCpf?: string },
): MarketplaceAddressFormValues {
  const base = emptyAddressFormValues();
  if (!userPatient || typeof userPatient !== "object") return base;

  const patient = userPatient as PatientProfileShape;
  const { firstName, lastName } = extractPatientName(patient, options?.userName);
  const cpf = extractPatientCpf(patient, options?.patientCpf);
  const phone = extractPatientPhone(patient);
  const addressFields = extractPatientAddressFields(patient);

  return {
    ...base,
    firstName,
    lastName,
    cpf,
    phone,
    ...addressFields,
  };
}

/** Mescla fontes de prefill — campos já preenchidos no destino não são sobrescritos. */
export function mergeAddressFormPrefill(
  ...sources: Partial<MarketplaceAddressFormValues>[]
): MarketplaceAddressFormValues {
  const result = emptyAddressFormValues();

  for (const source of sources) {
    (Object.keys(result) as (keyof MarketplaceAddressFormValues)[]).forEach(
      (key) => {
        const value = source[key];
        if (typeof value === "string" && value.trim() && !result[key].trim()) {
          result[key] = value.trim();
        }
      },
    );
  }

  return result;
}

/** Pré-preenchimento pessoal (sem campos de endereço) — para "novo endereço". */
export function patientPersonalPrefillOnly(
  userPatient: unknown,
  options?: { userName?: string; patientCpf?: string },
  customer?: WooCustomer,
): MarketplaceAddressFormValues {
  const fromPatient = patientProfilePrefillFormValues(userPatient, options);
  const fromCustomer = customer ? customerPrefillFormValues(customer) : null;

  return mergeAddressFormPrefill(
    {
      firstName: fromPatient.firstName,
      lastName: fromPatient.lastName,
      cpf: fromPatient.cpf,
      phone: fromPatient.phone,
    },
    fromCustomer ?? {},
  );
}

/** Verifica se o perfil Fluxmed (FHIR Patient) tem endereço completo. */
export function patientProfileHasAddress(userPatient: unknown): boolean {
  if (!userPatient || typeof userPatient !== "object") return false;
  const address = (userPatient as { address?: unknown[] }).address?.[0] as
    | {
        postalCode?: string;
        city?: string;
        state?: string;
        district?: string;
        line?: string[];
      }
    | undefined;

  if (!address) return false;

  const line = address.line ?? [];
  const hasStreet = Boolean(line[0]?.trim());
  const hasNumber = Boolean(line[1]?.trim());
  const hasCity = Boolean(address.city?.trim());
  const hasState = Boolean(address.state?.trim());
  const hasCep = Boolean(address.postalCode?.replace(/\D/g, "").length === 8);

  return hasStreet && hasNumber && hasCity && hasState && hasCep;
}

/** Mapeia endereço do perfil Fluxmed para formulário do marketplace. */
export function patientProfileToFormValues(
  userPatient: unknown,
  _userEmail: string,
  userName?: string,
  patientCpf?: string,
): MarketplaceAddressFormValues | null {
  if (!patientProfileHasAddress(userPatient)) return null;
  return patientProfilePrefillFormValues(userPatient, { userName, patientCpf });
}

export function formatAddressSummary(
  values: MarketplaceAddressFormValues,
): string {
  const { streetLine, cityLine, cepLine } = formatAddressDisplayLines(values);
  return [streetLine, cityLine, cepLine].filter(Boolean).join(" — ");
}

/** Endereço em linhas para cards de seleção (checkout). */
export function formatAddressDisplayLines(values: MarketplaceAddressFormValues): {
  streetLine: string;
  cityLine: string;
  cepLine: string;
} {
  const street = buildAddressLine(values.street, values.number);
  const streetLine = [street, values.complement].filter(Boolean).join(", ");

  const cityParts = [
    values.neighborhood,
    values.city && values.state
      ? `${values.city} - ${values.state}`
      : values.city || values.state,
  ].filter(Boolean);

  const cepDigits = values.cep.replace(/\D/g, "");
  const cepLine =
    cepDigits.length === 8
      ? `CEP ${cepDigits.replace(/^(\d{5})(\d{3})$/, "$1-$2")}`
      : values.cep.trim()
        ? `CEP ${values.cep.trim()}`
        : "";

  return {
    streetLine,
    cityLine: cityParts.join(" · "),
    cepLine,
  };
}

export function formatCustomerAddressSummary(customer: WooCustomer): string {
  const values = customerToFormValues(customer, customer.email);
  return formatAddressSummary(values);
}

export function customerNameSummary(customer: WooCustomer): string {
  const values = customerToFormValues(customer, customer.email);
  return [values.firstName, values.lastName].filter(Boolean).join(" ");
}

export function emptyAddressFormValues(): MarketplaceAddressFormValues {
  return {
    firstName: "",
    lastName: "",
    cpf: "",
    phone: "",
    cep: "",
    street: "",
    number: "",
    noStreetNumber: false,
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
  };
}

/** Requisitos de contato para checkout BR (PIX/cartão exigem CPF na cobrança). */
export const CHECKOUT_CONTACT_REQUIREMENTS = {
  requireCpf: true,
  requirePhone: true,
  requireNeighborhood: true,
} as const;

export function isCheckoutAddressComplete(
  values: MarketplaceAddressFormValues,
): boolean {
  return isAddressFormComplete(values, CHECKOUT_CONTACT_REQUIREMENTS);
}

/** Valida campos mínimos para checkout (entrega ou cobrança). */
export function isAddressFormComplete(
  values: MarketplaceAddressFormValues,
  options?: {
    requireCpf?: boolean;
    requirePhone?: boolean;
    requireNeighborhood?: boolean;
  },
): boolean {
  const requireCpf = options?.requireCpf ?? false;
  const requirePhone = options?.requirePhone ?? false;
  const requireNeighborhood = options?.requireNeighborhood ?? false;

  if (!values.firstName.trim() || !values.lastName.trim()) return false;
  if (requireCpf && !values.cpf.replace(/\D/g, "")) return false;
  if (requirePhone && !values.phone.replace(/\D/g, "")) return false;
  if (values.cep.replace(/\D/g, "").length !== 8) return false;
  if (!values.street.trim() || !isStreetNumberValidForCheckout(values)) return false;
  if (requireNeighborhood && !values.neighborhood.trim()) return false;
  if (!values.city.trim() || !values.state.trim()) return false;
  return true;
}

export function buildCheckoutAddressesFromCustomer(
  customer: WooCustomer,
  userEmail: string,
): MarketplaceCheckoutAddresses | null {
  if (!customerHasRegisteredAddress(customer)) return null;
  const address = customerToFormValues(customer, userEmail);
  return { shipping: address, billing: { ...address } };
}

/** Converte endereço Woo (pedido) para formulário do checkout. */
export function wooAddressToFormValues(
  address: WooAddress,
  userEmail: string,
  fallbackName?: { firstName: string; lastName: string },
): MarketplaceAddressFormValues {
  const pseudoCustomer = {
    id: 0,
    email: userEmail,
    first_name: fallbackName?.firstName ?? address.first_name ?? "",
    last_name: fallbackName?.lastName ?? address.last_name ?? "",
    billing: address,
    shipping: address,
  } satisfies WooCustomer;

  return mapWooAddressToFormValues(address, pseudoCustomer);
}

function formatOrderDateLabel(isoDate: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR").format(new Date(isoDate));
  } catch {
    return isoDate;
  }
}

function normalizeStreetKey(street: string): string {
  return street.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Chave de deduplicação — compara local físico (ignora nome/CPF/telefone). */
function addressFingerprint(values: MarketplaceAddressFormValues): string {
  const number = normalizeStreetNumber(values.number, {
    noStreetNumber: values.noStreetNumber,
  });
  return [
    values.cep.replace(/\D/g, ""),
    normalizeStreetKey(values.street),
    number.toLowerCase(),
    normalizeStreetKey(values.neighborhood),
    normalizeStreetKey(values.city),
    values.state.trim().toUpperCase(),
  ].join("|");
}

export function areSamePhysicalAddress(
  a: MarketplaceAddressFormValues,
  b: MarketplaceAddressFormValues,
): boolean {
  return addressFingerprint(a) === addressFingerprint(b);
}

function isUsableWooAddress(address?: Partial<WooAddress>): boolean {
  return isRegisteredWooAddressComplete(address);
}

/** Extrai até `limit` endereços únicos dos pedidos (mais recentes primeiro). */
export function extractAddressOptionsFromOrders(
  orders: WooOrder[],
  userEmail: string,
  limit: number = MAX_CHECKOUT_SAVED_ADDRESSES,
): MarketplaceAddressOption[] {
  const seen = new Set<string>();
  const options: MarketplaceAddressOption[] = [];
  const maxOptions = Math.max(1, limit);

  const sorted = [...orders].sort(
    (a, b) =>
      new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
  );

  for (const order of sorted) {
    if (options.length >= maxOptions) break;

    const candidates: Array<{ kind: "shipping" | "billing"; address?: WooAddress }> =
      [
        { kind: "shipping", address: order.shipping },
        { kind: "billing", address: order.billing },
      ];

    for (const { kind, address } of candidates) {
      if (options.length >= maxOptions) break;
      if (!address || !isUsableWooAddress(address)) continue;

      const values = wooAddressToFormValues(address, userEmail);
      const fp = addressFingerprint(values);
      if (seen.has(fp)) continue;
      seen.add(fp);

      options.push({
        id: `order-${order.id}-${kind}`,
        heading: `Pedido #${order.id} — ${formatOrderDateLabel(order.dateCreated)}`,
        values,
        source: "order",
      });
    }
  }

  return options;
}

/** Monta opções de endereço: até 3 pedidos recentes (únicos) + perfil MPI se diferente. */
export function buildAddressOptionsForCheckout(params: {
  orders: WooOrder[];
  userPatient?: unknown;
  userEmail: string;
  userName?: string;
  patientCpf?: string;
}): MarketplaceAddressOption[] {
  const { orders, userPatient, userEmail, userName, patientCpf } = params;
  const options = extractAddressOptionsFromOrders(
    orders,
    userEmail,
    MAX_CHECKOUT_SAVED_ADDRESSES,
  );

  const profileValues = patientProfileToFormValues(
    userPatient,
    userEmail,
    userName,
    patientCpf,
  );
  if (
    profileValues &&
    patientProfileHasAddress(userPatient) &&
    options.length < MAX_CHECKOUT_SAVED_ADDRESSES &&
    !options.some((opt) => areSamePhysicalAddress(opt.values, profileValues))
  ) {
    options.push({
      id: "profile-mpi",
      heading: "Perfil Fluxmed",
      values: profileValues,
      source: "profile",
    });
  }

  return options;
}

/** ID da opção salva padrão: endereço atual (se bater) ou o mais recente. */
export function resolveDefaultSavedAddressOptionId(
  options: MarketplaceAddressOption[],
  currentShipping?: MarketplaceAddressFormValues,
): string {
  if (options.length === 0) return "";
  if (currentShipping) {
    const match = options.find((opt) =>
      areSamePhysicalAddress(opt.values, currentShipping),
    );
    if (match) return match.id;
  }
  return options[0].id;
}

export function mergeSavedAddressWithPersonalData(
  option: MarketplaceAddressOption,
  params: {
    userPatient?: unknown;
    userName?: string;
    patientCpf?: string;
    customer?: WooCustomer;
  },
): MarketplaceAddressFormValues {
  const personal = patientPersonalPrefillOnly(
    params.userPatient,
    { userName: params.userName, patientCpf: params.patientCpf },
    params.customer,
  );
  return mergeAddressFormPrefill(option.values, {
    cpf: personal.cpf,
    phone: personal.phone,
  });
}

/** Default checkout: endereço salvo mais recente (único) → MPI → prefill patient/customer. */
export function buildDefaultCheckoutAddresses(params: {
  orders: WooOrder[];
  userPatient?: unknown;
  userEmail: string;
  userName?: string;
  patientCpf?: string;
  customer?: WooCustomer;
}): MarketplaceCheckoutAddresses {
  const { orders, userPatient, userEmail, userName, patientCpf, customer } =
    params;

  const savedOptions = buildAddressOptionsForCheckout({
    orders,
    userPatient,
    userEmail,
    userName,
    patientCpf,
  });

  if (savedOptions.length > 0) {
    const shipping = mergeSavedAddressWithPersonalData(savedOptions[0], {
      userPatient,
      userName,
      patientCpf,
      customer,
    });
    return { shipping, billing: { ...shipping } };
  }

  const profileValues = patientProfileToFormValues(
    userPatient,
    userEmail,
    userName,
    patientCpf,
  );
  if (profileValues) {
    return { shipping: profileValues, billing: { ...profileValues } };
  }

  const prefill = mergeAddressFormPrefill(
    patientProfilePrefillFormValues(userPatient, { userName, patientCpf }),
    customer ? customerPrefillFormValues(customer) : {},
  );

  return { shipping: prefill, billing: { ...prefill } };
}

export function recipientNameFromAddress(
  values: MarketplaceAddressFormValues,
): string {
  return [values.firstName, values.lastName].filter(Boolean).join(" ");
}

export function customerPrefillFormValues(
  customer: WooCustomer,
): MarketplaceAddressFormValues {
  return {
    ...emptyAddressFormValues(),
    firstName: customer.first_name || customer.billing?.first_name || "",
    lastName: customer.last_name || customer.billing?.last_name || "",
    phone: customer.billing?.phone || customer.billing?.cellphone || "",
    cpf: customer.billing?.cpf ?? "",
  };
}
